import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AccessDenied, AccessLoadError, useAccess } from "./AccessContext";
import { SkeletonPage } from "./skeleton/Skeleton";

// Customer workspace pages and the permissions that unlock them (any one is enough).
// Pages not listed here (notifications, help, account profile) are always available.
export const workspaceRoutePermissions = [
  ["/dashboard", ["user.dashboard.view"]],
  ["/content-calendar", ["user.content_calendar.view"]],
  ["/ai-post-studio", ["user.create_content.view"]],
  ["/manual-post-studio", ["user.posts.view"]],
  ["/media-library", ["user.media_library.view"]],
  ["/social-accounts", ["user.social_accounts.view"]],
  ["/messages", ["user.messages.view"]],
  ["/leads", ["user.leads.view"]],
  ["/analytics", ["user.analytics.view"]],
  ["/business-profile", ["user.business_profile.view"]],
  ["/products-services", ["user.business_profile.view"]],
  ["/brand-settings", ["user.business_profile.view"]],
  ["/content-preferences", ["user.create_content.view"]],
  ["/settings", ["user.settings.view"]],
  ["/upgrade-plan", ["user.subscriptions.view", "user.billing.view"]],
  // Sub-pages that need more than view access. Listed last so they never become a landing page.
  ["/ai-post-studio/create", ["user.create_content.create", "user.content_calendar.create"]],
  ["/manual-post-studio/create", ["user.posts.create"]],
  ["/manual-post-studio/:id/edit", ["user.posts.edit"]],
  ["/ai-post-studio/:id/edit", ["user.posts.edit"]]
];

const matchesPath = (pattern, pathname) => {
  const patternParts = pattern.split("/");
  const pathParts = pathname.split("/");
  // A pattern also covers its sub-pages, e.g. "/leads" covers "/leads/123".
  return pathParts.length >= patternParts.length && patternParts.every((part, index) => part.startsWith(":") ? Boolean(pathParts[index]) : part === pathParts[index]);
};

// The most specific matching entry wins, so sub-pages can require stronger permissions.
export const permissionsForPath = (pathname) => workspaceRoutePermissions
  .filter(([path]) => matchesPath(path, pathname))
  .sort(([first], [second]) => second.split("/").length - first.split("/").length)[0]?.[1] || null;

export const useWorkspaceAccess = () => {
  const context = useAccess();
  const canOpen = (pathname) => {
    const permissions = permissionsForPath(pathname);
    return !permissions || context.can(...permissions);
  };
  const firstAllowedPath = workspaceRoutePermissions.find(([path]) => !path.includes("/create") && !path.includes(":") && canOpen(path))?.[0] || "/notifications";
  return { ...context, canOpen, firstAllowedPath };
};

export const WorkspaceRouteGuard = ({ children }) => {
  const location = useLocation();
  const { access, loading, error, reload, canOpen, firstAllowedPath } = useWorkspaceAccess();

  if (loading && !access) return <SkeletonPage label="Checking your access" />;
  if (error && !access) return <AccessLoadError error={error} onRetry={reload} />;
  if (canOpen(location.pathname)) return children;
  // Sign-in lands on the dashboard; send people without it to the first page their role allows.
  if (location.pathname === "/dashboard") return <Navigate to={firstAllowedPath} replace />;
  return <AccessDenied hasOtherPages={firstAllowedPath !== "/notifications"} />;
};
